import { Link } from 'react-router-dom'
import BlendCalc from '../components/BlendCalc.jsx'
import FaqBlock from '../components/FaqBlock.jsx'
import Seo from '../components/Seo.jsx'
import { faqLd } from '../lib/faq.js'

const BLEND_FAQ = [
  {
    q: 'What is a peptide blend?',
    a: 'A blend is a single vial that contains two or more peptides mixed together — for example a Tirzepatide/Retatrutide 30/20 mg vial. Because they share the same bacteriostatic water, every unit you draw contains a fixed ratio of each compound, so you can’t dose them independently.',
  },
  {
    q: 'How do I calculate a peptide blend dose?',
    a: 'Reconstituting gives each compound its own concentration: mg ÷ water (mL). On a U-100 syringe, 100 units = 1 mL, so drawing U units pulls U/100 mL — and that volume contains each compound scaled by its concentration. Enter the compounds, the water, and the units you draw above and the calculator shows the mg of each.',
  },
  {
    q: 'Example: a Tirz/Reta 30/20 mg blend in 2 mL',
    a: 'Tirzepatide is 30 mg ÷ 2 mL = 15 mg/mL; Retatrutide is 20 mg ÷ 2 mL = 10 mg/mL. Drawing 20 units is 0.2 mL, so you get 15 × 0.2 = 3 mg of tirzepatide and 10 × 0.2 = 2 mg of retatrutide in that single 20-unit draw.',
  },
  {
    q: 'Does adding more water change the blend ratio?',
    a: 'No — the ratio between the compounds is fixed by how much of each is in the vial. Adding more or less water only changes the concentration, so you draw more or fewer units for the same amount of each compound. It never changes the proportion of one peptide to another.',
  },
]

export default function Blend() {
  return (
    <main className="page">
      <Seo
        title="Peptide Blend Calculator | CalculateMyPeptides"
        description="Free peptide blend calculator for premixed multi-peptide vials (e.g. Tirzepatide/Retatrutide). Enter each compound's mg, your bacteriostatic water, and units drawn to see exactly how much of each peptide is in every dose."
        jsonLd={faqLd('Peptide blend calculator — FAQ', BLEND_FAQ)}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Blend Calculator', path: '/blend' }]}
      />
      <div className="container">
        <div style={{ marginBottom: 20 }}>
          <span className="pill blue" style={{ marginBottom: 8 }}>Free tool</span>
          <h1 className="section-title">Peptide Blend Calculator</h1>
          <p className="section-sub" style={{ maxWidth: '60ch' }}>
            For a single vial that holds <strong>more than one peptide</strong> in shared water — like a
            Tirzepatide/Retatrutide 30/20 mg blend. Enter what’s in the vial, how much water you added, and how many
            units you draw, and see exactly how much of each compound is in that dose.
          </p>
          <p className="soft" style={{ maxWidth: '68ch' }}>
            This is different from stacking separate vials in one syringe — in a blend the compounds are already mixed,
            so their ratio is fixed and every draw contains a set amount of each. Need a single-peptide vial instead?
            Use the <Link to="/calculator">standard reconstitution calculator</Link>.
          </p>
        </div>

        <BlendCalc />

        <FaqBlock title="Peptide blend FAQ" items={BLEND_FAQ} />

        <div className="pep-disclaimer" style={{ marginTop: 22 }}>
          ⚠️ This tool only does the math on the values you enter — it is not medical advice and does not recommend
          any dose. Follow the exact dosing your prescriber gave you, and consult a qualified healthcare professional.
        </div>
      </div>
    </main>
  )
}
